import { serviceAreaLabel } from './contracts';
import type { AgentView, Coverage, LeadInput, Point, RoutingPolicy } from './contracts';

export const METERS_PER_MILE = 1609.344;
export const EARTH_RADIUS_METERS = 6371008.8;
export function distanceMiles(a: Point, b: Point): number {
  const r = Math.PI / 180;
  const h = Math.sin((b.lat - a.lat) * r / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin((b.lng - a.lng) * r / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h))) / METERS_PER_MILE;
}
export interface CoverageMatch { tier: 'preferred' | 'backup' | 'excluded' | 'outside' | 'unknown'; reason: string; distance: number | null; ruleId: string | null }
export function evaluateCoverage(coverage: Coverage, lead: Pick<LeadInput, 'zip' | 'point' | 'leadType'>): CoverageMatch {
  let uncertainExclusion = false;
  const matches = coverage.rules.filter(rule => {
    if (!rule.enabled || !rule.leadTypes.includes(lead.leadType)) return false;
    if (rule.shape === 'zip_set') return rule.zips.includes(lead.zip);
    if (rule.radiusMiles === 0) return rule.centerZip === lead.zip;
    if (!lead.point || !rule.center || rule.radiusMiles === undefined) {
      if (rule.effect === 'exclude') uncertainExclusion = true;
      return false;
    }
    return distanceMiles(lead.point, rule.center) <= rule.radiusMiles;
  });
  const exclusion = matches.find(rule => rule.effect === 'exclude');
  if (exclusion) return { tier: 'excluded', reason: `Excluded by ${serviceAreaLabel(exclusion)}`, distance: null, ruleId: exclusion.id };
  if (uncertainExclusion) return { tier: 'unknown', reason: 'A location is required to check an exclusion.', distance: null, ruleId: null };
  const winner = matches.find(rule => rule.effect === 'preferred') ?? (coverage.backupEnabled ? matches.find(rule => rule.effect === 'backup') : undefined);
  if (winner) return {
    tier: winner.effect === 'backup' ? 'backup' : 'preferred', reason: `${winner.effect === 'backup' ? 'Backup' : 'Preferred'} · ${serviceAreaLabel(winner)}`,
    distance: winner.shape === 'radius' && winner.radiusMiles !== 0 && winner.center && lead.point ? distanceMiles(lead.point, winner.center) : null, ruleId: winner.id,
  };
  const needsLocation = !lead.point && coverage.rules.some(r => r.enabled && r.shape === 'radius' && r.radiusMiles !== 0 && r.leadTypes.includes(lead.leadType));
  return { tier: needsLocation ? 'unknown' : 'outside', reason: needsLocation ? 'No usable ZIP representative point' : 'Outside published coverage', distance: null, ruleId: null };
}
export interface EvaluatedCandidate { agent: AgentView; match: CoverageMatch; eligible: boolean; reason: string }
export function evaluateCandidate(agent: AgentView, lead: LeadInput, policy: RoutingPolicy): EvaluatedCandidate {
  const match = evaluateCoverage(agent.coverage, lead);
  const s = agent.settings;
  const reason = !s.enrolled ? 'Not enrolled' : agent.status !== 'Active' ? 'FUB user is not active' :
    agent.sms?.optedOut ? 'SMS opted out — lead flow blocked' : agent.sms?.ready !== true ? 'SMS onboarding requires three replies' : agent.paused === null ? 'FUB pause is unknown' : agent.paused ? 'Lead flow paused in FUB' : !agent.available ? 'Outside availability' :
    !s.leadTypes.includes(lead.leadType) ? 'Lead type not accepted' : !s.languages.includes(lead.language) ? 'Language not accepted' :
    lead.price < s.minPrice || (s.maxPrice !== null && lead.price > s.maxPrice) ? 'Outside price range' :
    agent.leadsThisWeek >= s.weeklyCap ? 'Weekly cap reached' :
    match.tier === 'backup' && !policy.allowBackup ? 'Backup coverage is disabled by policy' :
    !['preferred', 'backup'].includes(match.tier) ? match.reason : null;
  return { agent, match, eligible: reason === null, reason: reason ?? match.reason };
}
// Sequential tie breakers. Account identity is never a priority tier.
export function rankCandidates(candidates: EvaluatedCandidate[], lead: LeadInput, policy: RoutingPolicy, now = Date.now()): EvaluatedCandidate[] {
  const offerTime = (a: AgentView) => {
    const presented = a.lastPresentedAt ? Date.parse(a.lastPresentedAt) : -Infinity;
    const received = a.lastReceivedAt ? Date.parse(a.lastReceivedAt) : -Infinity;
    return policy.legacyFourHourBoost && presented < now - 14_400_000 && received > now - 14_400_000 ? -Infinity : presented;
  };
  const compareNumber = (a: number, b: number) => a === b ? 0 : a < b ? -1 : 1;
  return candidates.filter(c => c.eligible).sort((a, b) =>
    compareNumber(a.match.tier === 'preferred' ? 0 : 1, b.match.tier === 'preferred' ? 0 : 1) ||
    compareNumber(offerTime(a.agent), offerTime(b.agent)) ||
    compareNumber(a.agent.leadsThisWeek, b.agent.leadsThisWeek) ||
    (policy.legacyPriceDirection && lead.price > 0 ? (lead.price > 150000 ? -1 : 1) * compareNumber(a.agent.performanceCount, b.agent.performanceCount) : 0) ||
    compareNumber(a.match.distance ?? Infinity, b.match.distance ?? Infinity) ||
    compareNumber(a.agent.lastReceivedAt ? Date.parse(a.agent.lastReceivedAt) : -Infinity, b.agent.lastReceivedAt ? Date.parse(b.agent.lastReceivedAt) : -Infinity) ||
    a.agent.id.localeCompare(b.agent.id));
}

// Monday-based accounting key in the configured business timezone, including DST.
export function weekKey(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = (type: string) => Number(parts.find(p => p.type === type)?.value);
  const localDate = new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
  localDate.setUTCDate(localDate.getUTCDate() - (localDate.getUTCDay() + 6) % 7);
  return localDate.toISOString().slice(0, 10);
}
